import React, { useEffect, useState } from "react"
import { createContainer } from "unstated-next"
import * as voicifyApi from '../api';
import IResult from "../models/result/IResult";
import InvalidResult from "../models/result/InvalidResult";
import { createTestCase } from '../api/controllers/testCases';
import TestCase from "../models/customAssistant/TestCase";
import AvailableTestCasesModel from "../models/customAssistant/AvailableTestCasesModel";
import generateGuid from "../services/guids";
import CustomAssistantRequest from "../models/customAssistant/CustomAssistantRequest";
import CustomAssistantDevice from "../models/customAssistant/CustomAssistantDevice";
import UserProfile from '../models/userProfile/UserProfile';
import set from 'lodash';
import ApplicationModel from "../models/applications/api/ApplicationModel";
import CustomAssistantResponse from "../models/customAssistant/CustomAssistantResponse";


function useTestCaseContainer() {
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [availableTestCases, setAvailableTestCases] = useState<AvailableTestCasesModel>(null);
    const [assertions, setAssertions] = useState<{ [key: string]: string }>({});
    const [application, setApplication] = useState<ApplicationModel>();
    const [testCaseResults, setTestCaseResults] = useState<{ [key: string]: { [key: string]: string } }>({});
    const [singleTestCaseListener, setSingleTestCaseListener] = useState<{ [key: string]: CustomAssistantResponse }>(null);
    const [runningTests, setRunningTests] = useState<string[]>([]);
    const [fullResponses, setFullResponses] = useState<{ [key: string]: CustomAssistantResponse }>({});


    useEffect(() => {
        if (!singleTestCaseListener) return;
        const keys = Object.keys(singleTestCaseListener);
        if (keys?.length) {
            const key = keys[0];
            if (runningTests?.length && runningTests.includes(key)) {
                const newRunningTests = runningTests.filter(tc => tc != key);
                setRunningTests(newRunningTests);
            }
            const newTestCaseResults = { ...testCaseResults, [key]: singleTestCaseListener[key].assertions };
            setTestCaseResults(newTestCaseResults);
            setFullResponses({ ...fullResponses, [key]: singleTestCaseListener[key] });
            setSingleTestCaseListener(null);
        }
    }, [singleTestCaseListener])
    const getTestDevice = (): CustomAssistantDevice => {
        return {
            id: 'voicify-client-tester',
            name: 'Voicify Test Case Runner',
            supportsAudio: true,
            supportsBackgroundImage: true,
            supportsDisplayText: true,
            supportsForegroundImage: true,
            supportsSsml: true,
            supportsTextInput: true,
            supportsVideo: true,
            supportsVoiceInput: true,
        }
    };

    const buildAssistantRequest = (
        testCase: TestCase
    ) => {
        if (testCase.sessionState.sessionAttributes["conversationTurnHistoryState"]) {
            testCase.sessionState.sessionAttributes["ConversationTurnHistoryState"] = testCase.sessionState.sessionAttributes["conversationTurnHistoryState"];
            delete testCase.sessionState.sessionAttributes["conversationTurnHistoryState"];
        }
        const userId = generateGuid();
        testCase.userProfile.id = userId;
        testCase.userProfile.userId = userId;
        const sessionId = generateGuid();
        testCase.sessionState.id = sessionId;
        const intentRequest: CustomAssistantRequest = {
            requestId: generateGuid(),
            user: {
                id: userId
            },
            device: getTestDevice(),
            context: {
                sessionId: sessionId,
                requestName: null,
                requestType: 'IntentRequest',
                requiresLanguageUnderstanding: true,
                originalInput: testCase.text,
                noTracking: true,
                locale: testCase?.locale?.length ? testCase.locale : "en-US",
                channel: 'Voicify Test Case Runner'
            },
            testCase: testCase
        }
        return intentRequest;
    };
    const createTestCase = async (appId: string, testCase: TestCase): Promise<IResult<any>> => {
        try {
            testCase.applicationId = appId;
            if (testCase.sessionState.sessionAttributes["conversationTurnHistoryState"]) {
                testCase.sessionState.sessionAttributes["ConversationTurnHistoryState"] = testCase.sessionState.sessionAttributes["conversationTurnHistoryState"];
                delete testCase.sessionState.sessionAttributes["conversationTurnHistoryState"];
            }
            const result = await voicifyApi.createTestCase(appId, testCase);
        } catch (e) {
            setError(e.message);
            return new InvalidResult(e.message);
        }
    }

    const simulateTestCase = async (testCase: TestCase) => {
        setLoading(true);
        setError("");
        setRunningTests([... new Set([testCase.name])])
        const request = buildAssistantRequest(testCase);
        const response = await sendAndHandleSingleRequest(application.id, application.secret, request);
        setLoading(false);
        return response
    };

    const gatherAllTestCases = () => {
        let appLevelTestCases = availableTestCases?.applicationTestCases ?? []
        let contentLevelTestCases = Object.values(availableTestCases?.contentItemTestCases ?? {}).flat()
        return appLevelTestCases.concat(contentLevelTestCases)
    }

    const getContentItemIdList = (numAppTestCases, contentItemTestCaseMap) => {
        var idList = Array(numAppTestCases).fill(null);
        for (const contentId in contentItemTestCaseMap) {
            const ids = Array(contentItemTestCaseMap[contentId].length).fill(contentId)
            idList = idList.concat(ids)
        }
        return idList;
    }

    const runAllTestCases = async (names: string[] = []) => {
        setLoading(true);
        setError("");
        const concurrency = 3;
        const topLevelTestCases = gatherAllTestCases() ?? [];
        const topLevelContentIds = getContentItemIdList(availableTestCases?.applicationTestCases?.length ?? 0, availableTestCases?.contentItemTestCases ?? {})
        var filteredPairs = topLevelTestCases
            .map((tc, index) => ({ tc, id: topLevelContentIds[index] }))
        if (names?.length) {
            filteredPairs = filteredPairs.filter(pair => names.includes(pair.tc));
        }
        const finalList = [];
        var finalIdsList = [];
        for (let i = 0; i < filteredPairs.length; i++) {
            if (!runningTests.includes(filteredPairs[i].tc)) {
                finalList.push(filteredPairs[i].tc);
                finalIdsList.push(filteredPairs[i].id);
            }
        }

        const newRunningTests = [...runningTests, ...finalList];
        // unique
        const uniqueRunningTests = [...new Set(newRunningTests)];
        setRunningTests(uniqueRunningTests);
        let newTestCaseResults = { ...testCaseResults };
        const namesToDeleteSet = new Set(finalList)
        finalList.forEach((name) => {
            delete newTestCaseResults[name];
        });
        finalIdsList.filter((_, index) => !namesToDeleteSet.has(finalList[index]));
        setTestCaseResults(newTestCaseResults);
        const lists: string[][] = [];
        const idLists: string[][] = [];
        for (let i = 0; i < concurrency; i++) {
            lists.push([]);
            idLists.push([])
        }
        for (let i = 0; i < finalList.length; i++) {
            lists[i % concurrency].push(finalList[i]);
            idLists[i % concurrency].push(finalIdsList[i])
        }
        let tasks = [];
        let newFullResponses = { ...fullResponses };
        const runList = async (list: string[], ids: string[]) => {
            if (!list?.length) return;
            for (let i = 0; i < list.length; i++) {
                const response = await simulateTestCaseByName(list[i], ids[i]);
                if (response) {
                    newFullResponses[list[i]] = response;
                }
            }
        }
        for (let i = 0; i < lists.length; i++) {
            tasks.push(runList(lists[i], idLists[i]));
        }
        await Promise.all(tasks);
        setLoading(false);
        setFullResponses(newFullResponses);
        return newFullResponses
    };

    const simulateTestCaseByName = async (name: string, contentItemId?: string): Promise<CustomAssistantResponse> => {
        const testCase = await loadTestCase(application.id, name, contentItemId);
        const request = buildAssistantRequest(testCase);
        const result = await voicifyApi.sendCustomAssistantRequest(application.id, application.secret, request, false, true, true, true);
        const newTestCaseResults = {
        };
        let response: CustomAssistantResponse = null;
        if (result.resultType == "Ok") {
            response = result.data;
            newTestCaseResults[name] = response;
        }
        setSingleTestCaseListener(newTestCaseResults);
        return response;
    }

    const sendAndHandleSingleRequest = async (
        applicationId: string,
        applicationSecret: string,
        request: CustomAssistantRequest) => {
        try {
            if (request.testCase.sessionState.sessionAttributes["conversationTurnHistoryState"]) {
                request.testCase.sessionState.sessionAttributes["ConversationTurnHistoryState"] = request.testCase.sessionState.sessionAttributes["conversationTurnHistoryState"];
                delete request.testCase.sessionState.sessionAttributes["conversationTurnHistoryState"];
            }
            const result = await voicifyApi.sendCustomAssistantRequest(applicationId, applicationSecret, request, false, true, true, true);
            if (result.resultType == "Ok") {
                const response = result.data;
                if (response) {
                    setAssertions(response.assertions);
                    setSingleTestCaseListener({ [request.testCase.name]: response });
                    return response
                } else {
                    setAssertions({});
                }
            } else {
                setError(result.errors[0]);
            }
        }
        catch (error) {
            setError(error.message);
        }
    };

    const loadTestCases = async (appId: string, contentItemId?: string): Promise<AvailableTestCasesModel> => {
        try {
            setLoading(true);
            const result = await voicifyApi.getTestCases(appId, contentItemId, true);
            if (result.resultType === "Ok") {
                setLoading(false);
                setAvailableTestCases(result.data);
                return result.data;
            } else {
                setLoading(false);
                setError(result.errors[0]);
                return null;
            }
        } catch (e) {
            setError(e.message);
            return null;
        }
    }

    const loadTestCase = async (appId: string, name: string, contentItemId?: string): Promise<TestCase> => {
        try {
            setLoading(true);
            const result = await voicifyApi.getTestCase(appId, name, contentItemId);
            if (result.resultType === "Ok") {
                setLoading(false);
                return result.data;
            } else {
                setLoading(false);
                setError(result.errors[0]);
                return null;
            }
        } catch (e) {
            setError(e.message);
            return null;
        }
    }
    const deleteTestCase = async (appId: string, name: string, contentItemId?: string): Promise<TestCase> => {
        try {
            setLoading(true);
            const result = await voicifyApi.deleteTestCase(appId, name, contentItemId);
            if (result.resultType === "Ok") {
                setLoading(false);
                return result.data;
            } else {
                setLoading(false);
                setError(result.errors[0]);
                return null;
            }
        } catch (e) {
            setError(e.message);
            return null;
        }
    }



    return {
        loading,
        availableTestCases,
        assertions,
        testCaseResults,
        runningTests,
        fullResponses,
        setLoading,
        createTestCase,
        loadAppLevelTestCases: loadTestCases,
        loadAppLevelTestCase: loadTestCase,
        deleteAppLevelTestCase: deleteTestCase,
        simulateTestCase,
        setApplication,
        setAvailableTestCases,
        runAppLevelTestCases: runAllTestCases,
        simulateTestCaseByName,
        setRunningTests,
        setTestCaseResults,
        gatherAllTestCases,
        getContentItemIdList,
    };
}

const TestCaseContainer = createContainer(useTestCaseContainer);
export default TestCaseContainer;
